import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sun, Volume2, Loader2, X, Square } from "lucide-react";

const seenKey = (userId, date) => `pathqity-briefing-${userId}-${date}`;
const spokenKey = (userId, date) => `pathqity-briefing-spoken-${userId}-${date}`;
const AUTO_KEY = "pathqity-briefing-autoplay";

const Figure = ({ label, value, tone = "slate", testid }) => (
  <div className="min-w-[96px]" data-testid={testid}>
    <div className={`num font-display text-2xl font-bold ${
        tone === "danger" ? "text-red-800 dark:text-red-300" : "text-slate-900 dark:text-slate-100"}`}>
      {value}
    </div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </div>
  </div>
);

// Opt-in morning briefing. Shows once a day and only speaks when the Play button is pressed —
// browsers block unprompted audio, and the bench is a shared room.
export default function DailyBriefing({ userId, enabled }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [autoPlay, setAutoPlay] = useState(() => localStorage.getItem(AUTO_KEY) !== "0");
  const [blockedAudio, setBlockedAudio] = useState(false);
  const audio = useRef(null);
  const autoTried = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/ai/briefing");
      const key = seenKey(userId, d.date);
      if (localStorage.getItem(key)) return;
      setData(d);
    } catch (e) { /* the briefing is never worth blocking the dashboard for */ }
  }, [userId]);

  useEffect(() => { if (enabled && userId) load(); }, [enabled, userId, load]);
  useEffect(() => () => audio.current?.pause(), []);

  const close = () => {
    if (data) localStorage.setItem(seenKey(userId, data.date), "1");
    audio.current?.pause();
    setDismissed(true);
  };

  const play = useCallback(async (auto = false) => {
    if (playing) {
      audio.current?.pause();
      setPlaying(false);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/ai/speak", { text: data.spoken },
                                 { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      audio.current = new Audio(url);
      audio.current.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      await audio.current.play();
      setPlaying(true);
      setBlockedAudio(false);
    } catch (e) {
      if (auto) setBlockedAudio(true); else toast.error(apiError(e));
    } finally { setBusy(false); }
  }, [playing, data]);

  // First visit of the day: greet and read the briefing without being asked. The login click
  // counts as the gesture browsers require, but we fall back to a prompt if audio is blocked.
  useEffect(() => {
    if (!data || !autoPlay || autoTried.current) return;
    if (localStorage.getItem(spokenKey(userId, data.date))) return;
    autoTried.current = true;
    localStorage.setItem(spokenKey(userId, data.date), "1");
    play(true);
  }, [data, autoPlay, userId, play]);

  if (!enabled || dismissed || !data) return null;

  return (
    <section className="border-l-4 border-[#134e4a] bg-gradient-to-r from-teal-50 to-white p-4 shadow-sm
                        dark:border-teal-400 dark:from-slate-900 dark:to-slate-900"
             data-testid="daily-briefing">
      <div className="flex flex-wrap items-center gap-3">
        <Sun size={18} className="text-[#134e4a] dark:text-teal-300" />
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Today&apos;s briefing</h2>
        <Button size="sm" onClick={() => play(false)} disabled={busy} data-testid="briefing-play-button"
                className="h-9 bg-[#134e4a] px-4 hover:bg-[#0f3f3c] dark:bg-teal-500 dark:text-slate-950 dark:hover:bg-teal-400">
          {busy ? <Loader2 size={14} className="animate-spin" />
            : playing ? <Square size={14} /> : <Volume2 size={14} />}
          <span className="ml-2">{busy ? "Preparing…" : playing ? "Stop" : "Play briefing"}</span>
        </Button>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300"
               data-testid="briefing-autoplay-toggle">
          <input type="checkbox" checked={autoPlay} className="h-4 w-4"
                 data-testid="briefing-autoplay-checkbox"
                 onChange={(e) => {
                   setAutoPlay(e.target.checked);
                   localStorage.setItem(AUTO_KEY, e.target.checked ? "1" : "0");
                   toast.success(e.target.checked
                     ? "Silas will greet you and read the briefing at your first login each day"
                     : "Automatic briefing turned off");
                 }} />
          <span>Speak it automatically each day</span>
        </label>
        <button onClick={close} data-testid="briefing-dismiss-button"
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white">
          <X size={14} /> Dismiss for today
        </button>
      </div>

      {blockedAudio && (
        <p className="mt-2 text-xs font-semibold text-amber-700 dark:text-amber-300" data-testid="briefing-audio-blocked">
          Your browser blocked the automatic audio — press “Play briefing” to hear it.
        </p>
      )}

      {data.greeting && (
        <p className="mt-2 font-display text-lg italic text-[#134e4a] dark:text-teal-300" data-testid="briefing-greeting">
          {data.greeting}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3">
        <Figure label="New since yesterday" value={data.received_since_yesterday} testid="briefing-received" />
        <Figure label="STAT open" value={data.open_stat} tone={data.stat_breaches ? "danger" : "slate"}
                testid="briefing-stat" />
        <Figure label="Over turnaround" value={data.tat_breaches} tone={data.tat_breaches ? "danger" : "slate"}
                testid="briefing-tat" />
        <Figure label="Incomplete" value={data.incomplete} tone={data.incomplete ? "danger" : "slate"}
                testid="briefing-incomplete" />
        <Figure label="Due today" value={data.due_today} testid="briefing-due-today" />
        <Figure label="Lots expiring" value={data.expiring_lots} tone={data.expired_lots ? "danger" : "slate"}
                testid="briefing-lots" />
        <Figure label="Instruments overdue" value={data.equipment_overdue}
                tone={data.equipment_overdue ? "danger" : "slate"} testid="briefing-equipment" />
      </div>

      <p className="mt-3 border-t border-teal-100 pt-2 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300" data-testid="briefing-text">
        {data.spoken}
      </p>
    </section>
  );
}
